import { useEffect, useState } from "react";
import {
  getDocument,
  getDocuments,
  getDocumentsCustomer,
} from "../../../api/social";
import { showToastError } from "../../../services/toast";
import { useSocialStore } from "../../../stores/social";
import DepositDocs from "./DepositDocs";
import useModals from "../../../hooks/useModals";
import DeleteDocs from "./DeleteDocs";
import { useTranslation } from "react-i18next";
import {
  DocumentIcon,
  DownloadIcon,
  emptyStateIcon,
  IconButton,
  MicroLabel,
  TableEmptyState,
  TrashIcon,
} from "../../ui/tables/tableUi";

/**
 * `scroll` : la liste défile dans sa propre zone (panneau plein écran).
 * À passer à `false` quand le parent gère déjà le défilement.
 */
const ListDocs = ({ scroll = true }: { scroll?: boolean }) => {
  const { t } = useTranslation();
  // Store
  const { updateSocial, idEmployee, tabSocial, idCustomer } = useSocialStore();
  // State
  const [documents, setDocuments] = useState<any[]>([]);
  // Hooks
  const { modals, openModals, openModal, closeModal } = useModals();

  useEffect(() => {
    const request =
      tabSocial === "Bulletins"
        ? getDocumentsCustomer(idCustomer)
        : getDocuments(idEmployee);

    request
      .then((res) => {
        setDocuments(res.data);
      })
      .catch((err) => {
        showToastError(err.response.data.message);
      });
  }, [updateSocial, tabSocial]);

  const handleDownloadDoc = (idDoc: number, name: string) => {
    getDocument(idDoc)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError(t("Erreur lors du téléchargement du fichier"));
      });
  };

  return (
    <div className={`flex w-full flex-col font-Nunito ${scroll ? "h-full" : ""}`}>
      {/* Zone de dépôt de documents */}
      <div className="shrink-0 border-b border-slate-100 p-3 sm:p-4">
        <DepositDocs />
      </div>

      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      {/* Liste des documents */}
      <div
        className={
          scroll ? "min-h-0 flex-1 overflow-y-auto p-3 sm:p-4" : "p-3 sm:p-4"
        }
      >
        {documents?.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {documents.map((doc) => {
              const segments: string[] = doc.name?.split(".") ?? [];
              const extension =
                segments.length > 1
                  ? segments[segments.length - 1].toUpperCase()
                  : "";

              return (
                <li
                  key={doc.id}
                  className="rounded-xl border border-slate-200 bg-white p-2.5 transition-colors hover:border-indigo-200 hover:bg-indigo-50/40"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100 ring-inset"
                    >
                      <DocumentIcon />
                    </span>
                    <p
                      className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800"
                      title={doc.name}
                    >
                      {doc.name}
                    </p>
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                    <MicroLabel>{extension || t("Document")}</MicroLabel>

                    <div className="flex shrink-0 items-center gap-1.5">
                      <IconButton
                        label={t("Télécharger")}
                        tone="indigo"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadDoc(doc.id, doc.name);
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        label={t("Supprimer")}
                        tone="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openModal(
                            "deleteDoc",
                            <DeleteDocs
                              idDoc={doc.id}
                              closeModal={() => closeModal("deleteDoc")}
                            />,
                          );
                        }}
                      >
                        <TrashIcon />
                      </IconButton>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <TableEmptyState
            title={t("Aucun document")}
            description={t("Déposez vos premiers documents")}
            icon={emptyStateIcon(
              "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z",
            )}
          />
        )}
      </div>
    </div>
  );
};

export default ListDocs;
